import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Eye, EyeOff } from "lucide-react";
import AuthShell from "@/components/auth/AuthShell";
import { accountRequest } from "@/accountApi";

export default function ResetPassword() {
    const [params] = useSearchParams();
    const [password, setPassword] = useState("");
    const [showPwd, setShowPwd] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const navigate = useNavigate();
    const token = params.get("token") || "";

    const submit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        setSubmitting(true);
        try {
            await accountRequest("/api/account/reset-password", {
                method: "POST",
                body: { token, password },
            });
            toast.success("Password reset");
            navigate("/dashboard", { replace: true });
        } catch (error) {
            toast.error("Could not reset password", {
                description:
                    error instanceof Error
                        ? error.message
                        : "The reset link may be invalid or expired.",
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <AuthShell
            eyebrow="Password reset"
            title="Choose a new password."
            subtitle="Use at least 8 characters. Once saved, old account sessions are cleared."
            sideTitle="One-time reset link."
            sideBody="Reset tokens are stored hashed, expire after a short window, and can only be used once."
            sideMeta={[
                { k: "8+", v: "characters" },
                { k: "Once", v: "use only" },
                { k: "Auto", v: "sign in" },
            ]}
        >
            {!token ? (
                <div className="border border-ink p-6 bg-surface">
                    <h2 className="font-display text-2xl font-bold text-ink">Reset link missing.</h2>
                    <p className="text-ink-soft mt-3">Request a new password reset link from the forgot password page.</p>
                    <Link to="/forgot-password" className="btn btn-primary mt-6 h-11 px-4 text-xs">
                        Request reset link <ArrowRight size={12} />
                    </Link>
                </div>
            ) : (
                <form data-testid="reset-form" onSubmit={submit} className="space-y-5">
                    <div>
                        <div className="flex items-end justify-between mb-2">
                            <label className="text-sm font-medium text-ink">New password</label>
                            <span className="font-mono text-[10px] tracking-[0.2em] text-ink-mute">SECURE</span>
                        </div>
                        <div className="relative">
                            <input
                                data-testid="reset-input-password"
                                type={showPwd ? "text" : "password"}
                                required
                                minLength={8}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                autoComplete="new-password"
                                className="input-sharp pr-12"
                                placeholder="At least 8 characters"
                            />
                            <button
                                type="button"
                                data-testid="reset-toggle-password"
                                onClick={() => setShowPwd((v) => !v)}
                                aria-label={showPwd ? "Hide password" : "Show password"}
                                className="absolute right-0 top-0 h-12 w-12 flex items-center justify-center text-ink-mute hover:text-ink transition-colors"
                            >
                                {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                            </button>
                        </div>
                    </div>
                    <button
                        type="submit"
                        data-testid="reset-submit"
                        disabled={submitting}
                        className="btn btn-primary w-full h-12 disabled:opacity-70"
                    >
                        {submitting ? "Saving..." : "Save new password"}
                        <ArrowRight size={14} />
                    </button>
                </form>
            )}
        </AuthShell>
    );
}
